import cidrRegex from 'cidr-regex';
import { useForm } from 'react-hook-form';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectControlled } from '#components/patterns/forms/select/select/SelectControlled.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { LocalAllowRule } from '#dn-types/config-override.ts';

const DEFAULT_VALUES = { cidr: '', allow: '' as const };

type FormData = {
  cidr: string;
  allow: 'Allow' | 'Deny' | '';
};

type Props = {
  defaultValues?: LocalAllowRule;
  onSave: (values: LocalAllowRule) => void;
  cidrValidation: (value: string) => string | true;
};

function getFormValue(value?: LocalAllowRule) {
  if (value) {
    return { cidr: 'cidr' in value ? value.cidr : value.interface, allow: value.allow ? 'Allow' : 'Deny' } as const;
  }
}

export function LocalAllowListForm({ defaultValues, onSave, cidrValidation }: Props) {
  const formContext = useForm<FormData>({ defaultValues: getFormValue(defaultValues) ?? DEFAULT_VALUES });
  const { register, handleSubmit, formState, control } = formContext;
  const { errors } = formState;

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    return handleSubmit((data) => {
      const isCidr = cidrRegex({ exact: true }).test(data.cidr);
      return isCidr ?
          Promise.resolve(onSave({ ...data, allow: data.allow === 'Allow' }))
        : Promise.resolve(onSave({ interface: data.cidr, allow: data.allow === 'Allow' }));
    })(e);
  };

  return (
    <form method="post" id="lighthouse.local_allow_list-form" onSubmit={onSubmit}>
      <Spacer direction="vertical" size="wide">
        <Field label="CIDR IP range or local interface" className="width-100">
          <Input
            className="width-100"
            hasError={Boolean(errors.cidr)}
            {...register('cidr', { validate: cidrValidation })}
          />
          {errors.cidr && <ErrorMessage>{errors.cidr.message}</ErrorMessage>}
        </Field>
        <SelectControlled
          className="width-100"
          control={control}
          rules={{ required: 'Should this be allowed or denied?' }}
          name="allow"
          label="Allow"
          placeholder="Select…"
          errorMessage={errors.allow?.message}
          renderValue={(v) => v || null}
          renderOptions={({ onChange }) => (
            <>
              {['Allow', 'Deny'].map((v) => (
                <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                  {v}
                </SelectItem>
              ))}
            </>
          )}
        />
      </Spacer>
    </form>
  );
}
